import type { ServerSetting } from '../../../services/adminServerSettings';

/** The server setting that lets the unit manager act; it is switched on the Server settings page. */
export const UnitsSwitchKey = 'WorkerUnitsEnabled';

/** The image of new units, chosen in the page's images section. */
export const UnitsImageTagKey = 'WorkerUnitsImageTag';

export const UnitsCreationPausedKey = 'WorkerUnitsCreationPaused';

export const UnitsPerGroupKey = 'WorkerUnitsContainersPerGroup';

export const UnitsCpuCoresKey = 'WorkerUnitsCpuCores';

export const UnitsMemoryGbKey = 'WorkerUnitsMemoryGb';

export const UnitsWarmCountKey = 'WorkerUnitsWarmCount';

/** The value the policy applies: the stored one while the server accepts it, else the default. */
export function appliedValue(settings: ServerSetting[], key: string): string | null {
  const setting = settings.find((candidate) => candidate.key === key);
  if (!setting) return null;
  return setting.value != null && setting.valueError == null ? setting.value : setting.defaultValue;
}

/** The image tag new units start from; null while the settings are not loaded or no tag is set. */
export function currentImageTag(settings: ServerSetting[] | null): string | null {
  return (settings && appliedValue(settings, UnitsImageTagKey)) || null;
}

export interface UnitsPolicySetting {
  key: string;
  label: string;
  description: string;
  onOff?: boolean;
}

/** The units' policy, edited on the Units page only, in the groups and the order the page shows it. */
export const UnitsPolicyGroups: { title: string; settings: UnitsPolicySetting[] }[] = [
  {
    title: 'Creating units',
    settings: [
      {
        key: UnitsCreationPausedKey,
        label: 'Pause unit creation',
        description: 'On: no new units; running and idle units go on.',
        onOff: true,
      },
      {
        key: 'WorkerUnitsMaxCount',
        label: 'Most units',
        description: 'Every state counts; idle units above it drain.',
      },
      {
        key: UnitsWarmCountKey,
        label: 'Warm units',
        description: 'Idle units kept ready; billed while waiting.',
      },
    ],
  },
  {
    title: 'Size of a unit',
    settings: [
      {
        key: UnitsCpuCoresKey,
        label: 'Cores per unit',
        description: 'New units, such as 4 or 1.5.',
      },
      {
        key: UnitsMemoryGbKey,
        label: 'Memory per unit in GB',
        description: 'New units, such as 8.',
      },
      {
        key: UnitsPerGroupKey,
        label: 'Units per group',
        description: 'New groups.',
      },
    ],
  },
  {
    title: 'When a unit ends',
    settings: [
      {
        key: 'WorkerUnitsIdleTimeoutMinutes',
        label: 'Idle timeout in minutes',
        description: 'Warm units stay.',
      },
      {
        key: 'WorkerUnitsMaxJobsPerUnit',
        label: 'Most jobs per unit',
        description: '0: no limit.',
      },
      {
        key: 'WorkerUnitsMaxAgeHours',
        label: 'Longest unit life in hours',
        description: 'Ends after its job; 0: no limit.',
      },
      {
        key: 'WorkerUnitsMinFreeDiskGb',
        label: 'Free disk for a job in GB',
        description: 'Below it the unit ends; 1 to 50.',
      },
    ],
  },
  {
    title: 'Safety limits',
    settings: [
      {
        key: 'WorkerUnitsMaxJobMinutes',
        label: 'Longest job in minutes',
        description: 'Then the unit is removed and the job requeued; 45 or more.',
      },
      {
        key: 'WorkerUnitsHeartbeatTimeoutMinutes',
        label: 'Heartbeat timeout in minutes',
        description: 'A silent unit is removed; 5 or more.',
      },
    ],
  },
];

/** Every server setting the Units page edits; the Server settings page leaves them out. */
export const UnitsPageKeys = new Set([
  ...UnitsPolicyGroups.flatMap((group) => group.settings.map((setting) => setting.key)),
  UnitsImageTagKey,
]);
